import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { Modal } from './Modal.js';
import type { ScreenConfirm } from './ScreenLayout.js';

/**
 * LES FENETRES DE CONFIRMATION DU PRODUIT : une seule boite, deux tons.
 *
 * Elles n'appartiennent pas au mode Archives : le chantier 2_0 les ecrit, le
 * chantier 3_0 (selection de lignes et actions en LOT) les reprend TELLES
 * QUELLES en leur passant un NOMBRE au lieu d'une fiche ("Archiver 3 fiches ?",
 * "Supprimer definitivement 3 fiches ?"). C'est pourquoi les textes vivent dans
 * des fabriques (`archiveConfirm` / `removeConfirm`) et pas dans le JSX : deux
 * fenetres differentes pour un meme geste feraient deux vocabulaires, et le
 * lecteur chercherait ce qui change entre les deux au lieu de lire le nombre.
 *
 * DEUX TONS, ET UN SEUL EST ROUGE.
 * - ARCHIVER est neutre et REVERSIBLE : la fenetre dit ou PART la fiche, pas ce
 *   qu'on perd. Rien n'y est rouge, parce que rien n'y est definitif.
 * - SUPPRIMER est la SEULE FENETRE ROUGE DU PRODUIT. Son bouton de confirmation
 *   porte le seul aplat `--danger` PLEIN qu'on rencontrera : c'est le DERNIER
 *   geste d'un chemin, jamais le premier. Un aplat rouge propose en premier
 *   attire le clic qu'il devrait retenir, et c'est exactement pourquoi
 *   "Supprimer" reste SECONDAIRE dans le menu de fiche et dans la barre de lot.
 *
 * ANNULER EST A GAUCHE DU ROUGE (la main y arrive avant) ET PORTE LE FOCUS PAR
 * DEFAUT : la touche Entree annule au lieu de detruire. Pas de champ "tape le
 * nom pour confirmer" : trop lourd pour un usage quotidien, et ca n'apprend rien
 * de plus que la phrase (decision du fondateur).
 *
 * CE QU'ON N'ECRIT NULLE PART : qu'une ligne supprimee survit un temps en base.
 * C'est un garde-fou d'exploitation pour le support Taki, pas une corbeille
 * utilisateur. Pour l'utilisateur, supprime veut dire supprime.
 */

/** Ce que porte une fenetre de confirmation, une fois ses textes composes. */
export interface ConfirmDialogProps {
  /** Ouverte. Basculer suffit : le Modal anime l'entree ET la sortie. */
  open: boolean;
  /** `neutral` : rien de definitif. `danger` : le rouge plein, une seule fois. */
  tone: 'neutral' | 'danger';
  /** La question, en tete ("Archiver cette fiche ?"). */
  title: string;
  /** Le corps. Un tableau rend un paragraphe par entree. */
  body: string | string[];
  /** Libelle du bouton qui fait le geste ("Archiver"). */
  confirmLabel: string;
  /** Libelle de la sortie. Defaut "Annuler". */
  cancelLabel?: string;
  /** Le geste est confirme. L'appelant ferme la fenetre lui-meme. */
  onConfirm: () => void;
  /** Sortie : bouton Annuler, voile, ou Echap. */
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  tone,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Annuler',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  /*
   * LE FOCUS PAR DEFAUT VA SUR ANNULER, et il faut le reprendre APRES le Modal.
   * Le Modal focalise son PANNEAU a l'ouverture (pour qu'Echap reponde tout de
   * suite), et les effets d'un enfant s'executent AVANT ceux de son parent :
   * poser le focus dans un effet ordinaire, c'est se le faire reprendre a la
   * ligne suivante. Une frame plus tard, le commit est fini et le panneau a
   * deja pris puis rendu la main. `autoFocus` ne marcherait pas davantage : il
   * se joue pendant le commit, donc avant l'effet du parent.
   */
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => cancelRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const paragraphs = Array.isArray(body) ? body : [body];

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="md"
      footer={
        /*
         * L'ORDRE DES DEUX BOUTONS EST LA MOITIE DU TRAVAIL. "Annuler" est a
         * GAUCHE du geste (la main y arrive avant) et il porte le focus par
         * defaut : dans une fenetre destructrice, la touche Entree doit sortir,
         * jamais detruire. Le pied du Modal aligne deja a droite, avec l'ecart
         * du systeme : rien a redessiner ici.
         */
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            className={cn(
              // LE SEUL --danger PLEIN DU PRODUIT. La variante `danger` du
              // socle est en CONTOUR (surface blanche, encre rouge) : c'est la
              // bonne forme partout ailleurs, et la mauvaise ICI, ou le geste
              // est deja choisi et ou le bouton doit porter la gravite de ce
              // qui suit. Le survol assombrit sans changer de teinte : le
              // systeme n'a pas de `--danger-hover`, et en fabriquer un serait
              // un jeton de plus pour un seul bouton du produit.
              tone === 'danger' &&
                'border-danger bg-danger text-surface hover:bg-danger hover:brightness-[0.92]',
            )}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* LES TEXTES, ET L'ACCORD QUI SUIT LE NOMBRE                                 */
/*                                                                            */
/* L'accord descend jusque DANS le corps ("Elle quitte les listes" / "Elles    */
/* quittent les listes") : une phrase au pluriel forcee sur une seule fiche    */
/* s'entend, et c'est exactement le genre de detail qui fait qu'une fenetre    */
/* de confirmation se lit comme un message et non comme un gabarit.           */
/* -------------------------------------------------------------------------- */

/**
 * La fenetre d'ARCHIVAGE. Neutre : elle dit ou PART la fiche, pas ce qu'on
 * perd. `count` vaut 1 depuis la fiche, N depuis la barre de lot (3_0).
 *
 * `lies` EST LE BOUT DE PHRASE DES FICHES LIEES, compose par le moteur
 * (`phraseDesLies`, `packages/ui/src/lib/liens.tsx`) et vide quand rien n'est
 * accroche : « 3 fiches liees (Interventions) restent visibles. » Il s'AJOUTE au
 * corps, il ne le remplace pas, parce qu'il repond a une seconde question
 * (« et ce qui pointe cette fiche ? ») sans effacer la premiere (« ou part-elle
 * ? »). Le titre et le bouton ne bougent pas : c'est le meme geste.
 *
 * IL ARRIVE DEJA COMPOSE, et le calcul n'est pas ici : la fenetre ne sait ni
 * compter au serveur, ni accorder un pluriel qu'elle ne connait pas. Deux
 * endroits pour cette phrase (ici et dans le refus de suppression) en auraient
 * fait deux vocabulaires pour un meme fait.
 */
export function archiveConfirm(count = 1, lies = ''): ScreenConfirm {
  const many = count > 1;
  const corps = many
    ? 'Elles quittent les listes. Tu les retrouveras dans les archives.'
    : 'Elle quitte les listes. Tu la retrouveras dans les archives.';
  return {
    tone: 'neutral',
    title: many ? `Archiver ${count} fiches ?` : 'Archiver cette fiche ?',
    body: lies === '' ? corps : `${corps} ${lies}`,
    confirmLabel: 'Archiver',
  };
}

/**
 * LA FENETRE DE DESARCHIVAGE, jumelle d'`archiveConfirm` : neutre, parce que le
 * geste est reversible et ne detruit rien. Elle dit ou REVIENNENT les fiches.
 * Le lot de la barre l'ouvre en mode Archives avec un NOMBRE (G22).
 */
export function unarchiveConfirm(count = 1): ScreenConfirm {
  const many = count > 1;
  return {
    tone: 'neutral',
    title: many ? `Désarchiver ${count} fiches ?` : 'Désarchiver cette fiche ?',
    body: many ? 'Elles reviennent dans les listes.' : 'Elle revient dans les listes.',
    confirmLabel: 'Désarchiver',
  };
}

/**
 * LA FENETRE DE RETRAIT D'UN MODELE. Neutre comme l'archivage, et pour la meme
 * raison : le modele quitte une liste, il n'emporte rien avec lui.
 *
 * ELLE EXISTE EN FABRIQUE PARCE QUE DEUX SURFACES LA POSENT, et qu'elles ne se
 * voient pas : le menu de la fleche de « Ajouter » (`TemplateRemoveDialog`,
 * dans `components/TemplateMenu.tsx`) et le menu « ... » d'un modele ouvert en
 * MODE MODELES (`views/useScreen.ts`). Le retrait n'a qu'une formulation dans
 * le produit ; deux copies de cette phrase en auraient deux au premier
 * ajustement, et l'utilisateur lirait deux gestes la ou il n'y en a qu'un.
 *
 * LA SECONDE PHRASE EST LA POUR REPONDRE A LA SEULE QUESTION QU'ON SE POSE
 * DEVANT CE BOUTON : "et les fiches que j'ai deja creees avec ?". Elle y
 * repond avant qu'on ait a chercher.
 */
export function templateRemoveConfirm(nom: string): ScreenConfirm {
  return {
    tone: 'neutral',
    title: `Retirer le modèle "${nom}" ?`,
    body: 'Il quitte cette liste. Les fiches déjà créées à partir de lui ne changent pas.',
    confirmLabel: 'Retirer',
  };
}

/**
 * LA FENETRE DE RETRAIT DE FICHES LIEES D'UN MODELE (mode Modeles, carte liee).
 *
 * NEUTRE, COMME LES DEUX RETRAITS D'AU-DESSUS : on enleve des lignes d'un
 * PATRON, on ne detruit aucune fiche. Le rouge est reserve a la suppression
 * (G21) ; le poser ici ferait croire qu'on efface les interventions du
 * materiel qui a servi a faire le modele.
 *
 * LA SECONDE PHRASE REPOND A LA SEULE QUESTION QU'ON SE POSE DEVANT CE BOUTON,
 * et c'est la MEME question que devant `templateRemoveConfirm` : « et les
 * fiches que j'ai deja creees avec ? ». Elle y repond avant qu'on ait a
 * chercher, et elle le dit dans les memes mots, parce que c'est le meme fait.
 *
 * L'ACCORD SUIT LE NOMBRE JUSQUE DANS LE CORPS (« ne les auront plus » / « ne
 * l'auront plus »), comme `archiveConfirm` : la selection vient d'etre faite a
 * la main dans la colonne de choix, et c'est ce nombre-la qu'on relit avant de
 * confirmer.
 */
export function enfantsRetraitConfirm(count = 1): ScreenConfirm {
  const many = count > 1;
  return {
    tone: 'neutral',
    title: many
      ? `Retirer ${count} fiches liées du modèle ?`
      : 'Retirer cette fiche liée du modèle ?',
    body: many
      ? 'Les fiches créées depuis ce modèle ne les auront plus. Les fiches déjà créées ne changent pas.'
      : "Les fiches créées depuis ce modèle ne l'auront plus. Les fiches déjà créées ne changent pas.",
    confirmLabel: 'Retirer',
  };
}

/**
 * LA FENETRE « FICHE NON CREEE » : on quitte une fiche en cours de creation
 * (brouillon `~nouveau`) apres y avoir saisi quelque chose.
 *
 * NEUTRE, PAS ROUGE, ET C'EST DELIBERE. Rien n'existe encore en base : il n'y a
 * rien a detruire, seulement une saisie a ne pas perdre. Le rouge est reserve a
 * la suppression (G21), et le poser ici ferait croire qu'on efface une fiche.
 *
 * ELLE NE PARAIT QUE SI QUELQUE CHOSE A ETE SAISI (`BrouillonState.touche`) :
 * sur une fiche vide, la sortie est directe. Demander « es-tu sur ? » a qui n'a
 * rien tape est une ceremonie pour rien, et c'est ce qui apprend a cliquer
 * « Quitter » sans lire.
 *
 * « REVENIR » PORTE LE FOCUS PAR DEFAUT, comme dans toutes les fenetres du
 * produit : la touche Entree ramene a la saisie, elle ne la jette pas. Le
 * titre saisi est repris quand il existe ; sinon la phrase reste agnostique,
 * plutot que de nommer « ~nouveau » ou un id que personne n'a tape.
 */
export function brouillonAbandonConfirm(titre: string): ScreenConfirm {
  return {
    tone: 'neutral',
    title: 'Fiche non créée',
    body: [
      titre === '' ? "Cette fiche n'a pas été créée." : `« ${titre} » n'a pas été créée.`,
      'Si tu quittes maintenant, ce que tu as saisi est perdu.',
    ],
    confirmLabel: 'Quitter sans créer',
    cancelLabel: 'Revenir',
  };
}

/**
 * La fenetre de SUPPRESSION, la seule rouge du produit. Elle dit sans detour
 * que c'est definitif, et ne promet AUCUN moyen de revenir en arriere.
 */
export function removeConfirm(count = 1): ScreenConfirm {
  const many = count > 1;
  return {
    tone: 'danger',
    title: many
      ? `Supprimer définitivement ${count} fiches ?`
      : 'Supprimer définitivement cette fiche ?',
    body: many
      ? "Elles seront effacées pour de bon, avec leur historique. C'est définitif : rien ne permettra de les retrouver."
      : "Elle sera effacée pour de bon, avec son historique. C'est définitif : rien ne permettra de la retrouver.",
    confirmLabel: 'Supprimer définitivement',
  };
}

/** Ce que prennent les deux fenetres nommees : un nombre, et les deux issues. */
export interface CountConfirmDialogProps {
  open: boolean;
  /** Nombre de fiches concernees. Defaut 1 (le geste depuis la fiche). */
  count?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Archiver cette fiche ?" / "Archiver 3 fiches ?". La forme que le chantier
 * 3_0 monte telle quelle depuis la barre de lot.
 */
export function ArchiveConfirmDialog({
  open,
  count = 1,
  onConfirm,
  onCancel,
}: CountConfirmDialogProps): JSX.Element {
  return <ConfirmDialog open={open} {...archiveConfirm(count)} onConfirm={onConfirm} onCancel={onCancel} />;
}

/**
 * "Désarchiver cette fiche ?" / "Désarchiver 3 fiches ?". Meme boite que
 * l'archivage, montee depuis la barre de lot en mode Archives.
 */
export function UnarchiveConfirmDialog({
  open,
  count = 1,
  onConfirm,
  onCancel,
}: CountConfirmDialogProps): JSX.Element {
  return <ConfirmDialog open={open} {...unarchiveConfirm(count)} onConfirm={onConfirm} onCancel={onCancel} />;
}

/**
 * "Supprimer definitivement cette fiche ?" / "... 3 fiches ?". Meme boite, meme
 * ordre de boutons, meme focus par defaut : c'est le MEME geste, il porte
 * seulement un nombre.
 */
export function RemoveConfirmDialog({
  open,
  count = 1,
  onConfirm,
  onCancel,
}: CountConfirmDialogProps): JSX.Element {
  return <ConfirmDialog open={open} {...removeConfirm(count)} onConfirm={onConfirm} onCancel={onCancel} />;
}

export default ConfirmDialog;
